/* ============================================================
   The personal shopper (docs/PERSONAL-SHOPPER.md)
   It lives in the search room, its parts laid in by this script from
   snippets/personal-shopper.liquid: words that read as a request lead with
   the shopper and Enter asks it; a plain word keeps the search and offers
   the shopper quietly. The product page's door opens the same room with the
   piece pinned.
   Answers stream from Astrotorium's `personal-shopper` function; every card
   is drawn from the store's own /products/<handle>.js, so a price, an
   availability and a variant on screen are always the store's. Add to bag is
   the theme's own form[data-gg-atb]. The conversation is kept for the visit
   (sessionStorage); the house keeps a redacted transcript 180 days.
   ============================================================ */
(function (root) {
  'use strict';

  /* ---------- pure helpers (tests/personal-shopper.test.mjs) ---------- */

  /** A text/event-stream read chunk by chunk: feed() returns the whole events it completed. */
  function createSSEReader() {
    let buffer = '';
    return {
      feed(chunk) {
        buffer += chunk.replace(/\r\n?/g, '\n');
        const events = [];
        let at;
        while ((at = buffer.indexOf('\n\n')) >= 0) {
          const block = buffer.slice(0, at);
          buffer = buffer.slice(at + 2);
          let event = 'message';
          const data = [];
          for (const line of block.split('\n')) {
            if (!line || line[0] === ':') continue;
            const colon = line.indexOf(':');
            const field = colon < 0 ? line : line.slice(0, colon);
            const value = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '');
            if (field === 'event') event = value;
            else if (field === 'data') data.push(value);
          }
          if (!data.length) continue;
          let parsed = null;
          try { parsed = JSON.parse(data.join('\n')); } catch (e) { parsed = null; }
          events.push({ event: event, data: parsed });
        }
        return events;
      },
    };
  }

  /** Shopify's money format, cents in: {{amount}}, {{amount_no_decimals}} and the comma separated two. */
  function formatMoney(cents, format) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    const fmt = typeof format === 'string' && format.indexOf('{{') >= 0 ? format : '{{amount}}';
    const group = (amount, decimals, thousands, decimal) => {
      const fixed = (amount / 100).toFixed(decimals);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
      if (kind === 'amount_no_decimals') return group(value, 0, ',', '.');
      if (kind === 'amount_with_comma_separator') return group(value, 2, '.', ',');
      if (kind === 'amount_no_decimals_with_comma_separator') return group(value, 0, '.', ',');
      if (kind === 'amount_with_apostrophe_separator') return group(value, 2, "'", '.');
      return group(value, 2, ',', '.');
    }).replace(/<[^>]*>/g, '');
  }

  /**
   * What a card can offer, read from the store's own /products/<handle>.js:
   * one variant on sale → Add to bag with its id; several → the piece's own
   * page, where the size is chosen; none → sold out.
   */
  function offerOf(product) {
    if (!product || !Array.isArray(product.variants)) return null;
    const sellable = product.variants.filter((v) => v && v.available);
    const single = product.variants.length === 1;
    const lowest = (list) => list.reduce((min, v) => (min == null || v.price < min ? v.price : min), null);
    const image = product.featured_image || (product.images && product.images[0]) || null;
    return {
      title: String(product.title || ''),
      url: String(product.url || ('/products/' + product.handle)),
      image: typeof image === 'string' ? image : (image && image.src) || null,
      available: sellable.length > 0,
      price: lowest(sellable.length ? sellable : product.variants),
      variesInPrice: new Set(product.variants.map((v) => v.price)).size > 1,
      variantId: single && sellable.length ? sellable[0].id : null,
      choose: !single && sellable.length > 0,
    };
  }

  /** A Shopify CDN image at a width; any other URL as it came. */
  function sizedImage(url, width) {
    if (!url) return '';
    let src = String(url);
    if (src.startsWith('//')) src = 'https:' + src;
    if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(src)) return src;
    return src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=' + width;
  }

  /** The conversation as the function reads it: the newest turns, each answer with the handles it showed. */
  function requestMessages(log, max) {
    const turns = log.filter((m) => m && (m.role === 'user' || m.role === 'assistant') && m.text);
    return turns.slice(-(max || 14)).map((m) => ({
      role: m.role,
      text: String(m.text).slice(0, 1200),
      pieces: m.role === 'assistant' && Array.isArray(m.pieces) ? m.pieces.map((p) => p.handle).filter(Boolean) : undefined,
    }));
  }

  /**
   * Do these words read as a REQUEST (the shopper leads, Enter asks it) or as a
   * search for a word (the search leads, the shopper waits under the results)?
   * A question, a sentence of four words or more, a budget, an occasion or a
   * person is a request; "rings" and "silver moon" are searches.
   */
  const ASKING = /\b(for|under|below|less than|over|gift|gifts|something|looking|want|need|show me|help|wear|wears|wearing|match|matches|pair|pairs|recommend|suggest|which|what|wedding|birthday|anniversary|evening|opening|party|her|him|them|my|me)\b/i;
  function readsAsRequest(text) {
    const q = String(text || '').trim();
    if (q.length < 3) return false;
    const words = q.split(/\s+/).filter(Boolean);
    if (/\?\s*$/.test(q)) return true;
    if (words.length >= 4) return true;
    if (words.length >= 2 && (ASKING.test(q) || /\d/.test(q))) return true;
    return false;
  }

  /** What taking a pill away asks the shopper, in the visitor's voice. */
  function withoutPill(pill) {
    if (!pill) return '';
    if (pill.key === 'under' || pill.key === 'over') return 'The same, without the budget';
    if (pill.key === 'words') return 'Something else';
    return 'The same, but not only ' + String(pill.value).toLowerCase();
  }

  /** A pill's door into the advanced search, where the facet has one. */
  function pillHref(pill, searchUrl) {
    const keys = { dept: 'dept', metal: 'metal', stone: 'stone', style: 'style', colour: 'colour' };
    if (!pill || !keys[pill.key]) return null;
    const value = pill.key === 'dept' ? ({ Jewelry: 'jewelry', Couture: 'couture', 'Art & Sculpture': 'art-sculpture' })[pill.value] || pill.value : pill.value;
    return (searchUrl || '/search') + '?' + keys[pill.key] + '=' + encodeURIComponent(value);
  }

  const helpers = { createSSEReader, formatMoney, offerOf, sizedImage, requestMessages, readsAsRequest, withoutPill, pillHref };
  root.GGShopper = helpers;

  /* ---------- the room ---------- */

  if (typeof document === 'undefined') return;
  const config = document.getElementById('GGShopperConfig');
  const search = document.querySelector('gg-search');
  if (!config || !search || search.querySelector('[data-shopper-room]')) return;

  const STORE_KEY = 'gg-shopper';
  const OFF_KEY = 'gg-shopper-off';
  const KEY_KEY = 'gg-shopper-key';
  const ROOT = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
  const moneyFormat = () => (root.GG && root.GG.moneyFormat) || '{{amount}}';
  const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* no storage */ } },
  };
  if (store.get(OFF_KEY)) return;
  // testing only, for now: the shopper shows only once a visit has asked for it with ?shopper=1 (kept for the visit;
  // ?shopper=0 puts it away). Without it nothing of the shopper is laid into the page.
  const TEST_KEY = 'gg-shopper-test';
  const asked = new URLSearchParams(root.location ? root.location.search : '').get('shopper');
  if (asked === '1') store.set(TEST_KEY, '1');
  if (asked === '0') { try { sessionStorage.removeItem(TEST_KEY); } catch (e) { /* no storage */ } }
  if (store.get(TEST_KEY) !== '1') return;

  /* ---- its parts, laid into the page (snippets/personal-shopper.liquid): nothing else in the theme knows the shopper
     is there, so it fits any version of the search room and the product page. A part whose place is missing is left
     out; without the search form and its results there is no room, and the search stays exactly as it is. ---- */

  const part = (name) => {
    const tpl = document.querySelector('template[data-shopper-part="' + name + '"]');
    const node = tpl && tpl.content && tpl.content.firstElementChild;
    return node ? node.cloneNode(true) : null;
  };
  const after = (node, anchor) => { anchor.parentNode.insertBefore(node, anchor.nextSibling); return node; };
  const searchForm = search.querySelector('.gg-search__form') || search.querySelector('form');
  const searchResults = search.querySelector('[data-search-results]');
  const room = part('search-room');
  if (!searchForm || !searchResults || !room) return;
  const leadPart = part('search-lead');
  if (leadPart) after(leadPart, searchForm);
  const quietPart = part('search-quiet');
  if (quietPart) after(quietPart, searchResults);
  after(room, quietPart || searchResults);
  const zero = search.querySelector('[data-search-zero]');
  const doorPart = zero && part('search-door');
  if (doorPart) {
    const pillars = zero.querySelector('.gg-search__pillars-kicker');
    if (pillars) pillars.parentNode.insertBefore(doorPart, pillars);
    else (zero.querySelector('.gg-search__col') || zero).appendChild(doorPart);
  }
  const pdpPart = part('pdp-door');
  if (pdpPart) {
    // after the purchase notes, else the pay note, else the way to buy itself
    const pdp = document.querySelector('.gg-pdp');
    const at = pdp && (pdp.querySelector('.gg-pdp__purchase-notes') || pdp.querySelector('.gg-pdp__paynote') || pdp.querySelector('form[data-gg-atb], form[action*="/cart/add"]'));
    if (at) after(pdpPart, at);
  }
  const shop = room;

  const input = search.querySelector('[data-search-input]');
  const form = search.querySelector('.gg-search__form');
  const lead = search.querySelector('[data-shopper-ask="lead"]');
  const quiet = search.querySelector('[data-shopper-ask="quiet"]');
  const quietWords = quiet && quiet.querySelector('[data-shopper-q]');
  const door = search.querySelector('[data-shopper-door]');
  const thread = shop.querySelector('[data-shopper-thread]');
  const status = shop.querySelector('[data-shopper-status]');
  const chips = shop.querySelector('[data-shopper-chips]');
  const followForm = shop.querySelector('[data-shopper-form]');
  const followInput = shop.querySelector('[data-shopper-input]');
  // a placeholder is read whole or not at all: on a phone the dock says the short form
  (function fitPlaceholder() {
    const long = followInput.getAttribute('placeholder');
    const short = followInput.dataset.placeholderShort;
    if (!short || !window.matchMedia) return;
    const narrow = window.matchMedia('(max-width: 749px)');
    const fit = () => followInput.setAttribute('placeholder', narrow.matches ? short : long);
    fit();
    if (narrow.addEventListener) narrow.addEventListener('change', fit);
  })();
  const sendKey = shop.querySelector('[data-shopper-send]');
  const title = shop.querySelector('#GGShopTitle');
  const searchUrl = search.dataset.searchUrl || '/search';
  if (door) door.hidden = false;

  const sessionKey = (() => {
    let k = store.get(KEY_KEY);
    if (!k || !/^[A-Za-z0-9_-]{16,64}$/.test(k)) {
      const bytes = new Uint8Array(18);
      (root.crypto || {}).getRandomValues ? root.crypto.getRandomValues(bytes) : bytes.forEach((_, i) => { bytes[i] = Math.floor(Math.random() * 256); });
      k = Array.from(bytes, (b) => 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_-'[b % 64]).join('');
      store.set(KEY_KEY, k);
    }
    return k;
  })();

  let log = (() => {
    try {
      const list = JSON.parse(store.get(STORE_KEY) || '[]');
      return Array.isArray(list) ? list.slice(-30) : [];
    } catch (e) { return []; }
  })();
  const save = () => store.set(STORE_KEY, JSON.stringify(log.slice(-30)));
  const products = new Map();
  let busy = false;
  let controller = null;
  let cartStale = false;
  let pin = null;

  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  };

  /* ---- the search's two rows ---- */

  const offer = () => {
    const q = (input.value || '').trim();
    const asks = readsAsRequest(q);
    if (lead) lead.hidden = !asks || shopping();
    // while the shopper leads, a sentence the keyword search cannot match is the shopper's, not a dead end
    search.classList.toggle('is-asking', asks && !shopping());
    if (quiet) quiet.hidden = asks || q.length < 2 || shopping();
    if (quietWords) quietWords.textContent = q;
  };
  input.addEventListener('input', offer);
  // Enter on a request asks the shopper; on a word it searches as it always did
  form.addEventListener('submit', (e) => {
    const q = (input.value || '').trim();
    if (!readsAsRequest(q)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    enter(q);
  }, true);
  search.querySelectorAll('[data-shopper-go]').forEach((b) => b.addEventListener('click', () => enter((input.value || '').trim())));
  search.querySelectorAll('[data-shopper-say]').forEach((b) => b.addEventListener('click', () => {
    input.value = b.textContent.trim();
    enter(input.value);
  }));
  // the search room opens and closes by its hidden attribute (gallery-gothic.js, gg-search): watched, never patched
  let roomOpen = !search.hidden;
  new MutationObserver(() => {
    if (roomOpen === !search.hidden) return;
    roomOpen = !search.hidden;
    if (roomOpen) { if (!shopping()) offer(); } else leave(false);
  }).observe(search, { attributes: true, attributeFilter: ['hidden'] });
  // ↓ ↑ walk the shopper's rows first, then what the search walks (its own walk, read the same way)
  search.addEventListener('keydown', (e) => {
    if ((e.key !== 'ArrowDown' && e.key !== 'ArrowUp') || shopping()) return;
    const asks = [...search.querySelectorAll('.gg-ask:not([hidden]) button')].filter((b) => b.offsetParent);
    if (!asks.length) return;
    const items = asks.concat([...search.querySelectorAll('.gg-search__live a, .gg-search__zero a, .gg-search__zero button')].filter((a) => a.offsetParent));
    e.preventDefault();
    e.stopPropagation();
    const step = e.key === 'ArrowDown' ? 1 : -1;
    const i = items.indexOf(document.activeElement);
    items[i < 0 ? (step > 0 ? 0 : items.length - 1) : (i + step + items.length) % items.length].focus();
  }, true);

  /* ---- into and out of the shopper's hands ---- */

  function shopping() { return search.classList.contains('is-shopping'); }

  function enter(question, opts) {
    pin = (opts && opts.pin) || pin;
    search.classList.add('is-shopping');
    search.classList.remove('is-asking');
    shop.hidden = false;
    if (lead) lead.hidden = true;
    if (quiet) quiet.hidden = true;
    draw();
    if (title) title.focus({ preventScroll: true });
    // a door is a new question: it takes over from one still being answered
    if (question) ask(question, { interrupt: true });
  }

  function leave(toSearch) {
    if (!shopping()) return;
    search.classList.remove('is-shopping');
    shop.hidden = true;
    pin = null;
    if (toSearch) {
      input.focus();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  shop.querySelector('[data-shopper-back]').addEventListener('click', () => leave(true));
  followForm.addEventListener('submit', (e) => {
    e.preventDefault();
    ask(followInput.value);
  });
  shop.addEventListener('click', (e) => {
    const say = e.target.closest('[data-shopper-chip]');
    if (say) { ask(say.textContent.trim()); return; }
    const edit = e.target.closest('[data-shopper-edit]');
    if (edit) {
      input.value = edit.dataset.shopperEdit;
      leave(true);
      return;
    }
    const drop = e.target.closest('[data-shopper-drop]');
    if (drop) { ask(drop.dataset.shopperDrop); return; }
    if (e.target.closest('a[href="#visit"]')) {
      // the visit dialog opens over the page; the room steps aside for it
      const closer = search.querySelector('[data-search-close]');
      if (closer) closer.click();
    }
  });

  /* ---- the product page's door ---- */

  document.querySelectorAll('[data-shopper-pdp]').forEach((d) => {
    d.hidden = false;
    d.addEventListener('click', (e) => {
      const b = e.target.closest('[data-shopper-pdp-ask]');
      if (!b) return;
      const said = b.dataset.shopperPdpAsk || b.textContent.trim();
      search.open();
      enter(said, { pin: { handle: d.dataset.handle, title: d.dataset.title, image: d.dataset.image } });
    });
  });

  /* ---- a turn ---- */

  async function context() {
    let cart = (config.dataset.cart || '').split(',').filter(Boolean);
    if (cartStale || !cart.length) {
      try {
        const res = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } });
        const json = await res.json();
        cart = (json.items || []).map((i) => i.handle).filter(Boolean);
        cartStale = false;
        config.dataset.cart = cart.join(',');
      } catch (e) { /* the page's own list stands */ }
    }
    let favorites = [];
    try {
      const kept = JSON.parse(localStorage.getItem('gg-favorites') || '[]');
      favorites = Array.isArray(kept) ? kept.map((f) => f && f.handle).filter(Boolean).slice(0, 20) : [];
    } catch (e) { /* none */ }
    return {
      product: (pin && pin.handle) || config.dataset.product || null,
      page: config.dataset.page || null,
      cart: cart.slice(0, 20),
      favorites,
    };
  }

  async function ask(said, opts) {
    const text = String(said || '').trim().slice(0, 1200);
    if (!text) return;
    if (busy) {
      if (!(opts && opts.interrupt) || !controller) return;
      controller.abort();
    }
    busy = true;
    sendKey.disabled = true;
    followInput.value = '';
    // the piece a question is about is pinned once, on the first question about it
    const turn = { role: 'user', text, pin: pin && !log.some((m) => m.pin && m.pin.handle === pin.handle) ? pin : null };
    log.push(turn);
    save();
    draw();
    setStatus('Looking through the collection');

    const ctl = new AbortController();
    controller = ctl;
    let answered = false;
    try {
      const res = await fetch(config.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ sessionKey, messages: requestMessages(log, 14), context: await context() }),
        signal: ctl.signal,
      });
      if (!res.ok || !res.body) {
        const json = await res.json().catch(() => ({}));
        if (json.code === 'off') store.set(OFF_KEY, '1');
        throw Object.assign(new Error(json.error || 'The shopper could not answer.'), { shown: Boolean(json.error) });
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      const sse = createSSEReader();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        for (const { event, data } of sse.feed(decoder.decode(value, { stream: true }))) {
          if (event === 'status' || event === 'step') setStatus((data && (data.text || data.label)) || '');
          else if (event === 'answer' && data) { answered = true; answer(data); }
          else if (event === 'error' && data && !answered) { answered = true; fail(data.message, true); }
        }
      }
      if (!answered) fail('The shopper lost the thread. Ask again.', true);
    } catch (e) {
      if (!ctl.signal.aborted) {
        console.warn('[personal-shopper]', e);
        fail(e && e.shown ? e.message : 'The shopper could not be reached just now.', true);
      }
    } finally {
      // an answer taken over by a newer question leaves the room to it
      if (controller === ctl) {
        controller = null;
        busy = false;
        sendKey.disabled = false;
        setStatus('');
      }
    }
  }

  function answer(data) {
    log.push({
      role: 'assistant',
      text: String(data.reply || ''),
      pieces: Array.isArray(data.pieces) ? data.pieces.slice(0, 6).map((p) => ({ handle: p.handle, title: p.title, note: p.note || '', image: p.image || null })) : [],
      followUps: Array.isArray(data.followUps) ? data.followUps.slice(0, 3) : [],
      lookedFor: Array.isArray(data.lookedFor) ? data.lookedFor.slice(0, 8) : [],
      handOff: Boolean(data.handOff),
    });
    save();
    draw();
  }

  function fail(message, handOff) {
    // a failed turn gives the visitor's words back to the field, so nothing typed is lost
    const last = log[log.length - 1];
    if (last && last.role === 'user') {
      log.pop();
      save();
      if (!followInput.value) followInput.value = last.text;
    }
    draw();
    if (!message) return;
    const note = el('div', 'gg-shop__note');
    note.setAttribute('role', 'alert');
    note.appendChild(el('p', 'gg-shop__note-text', message));
    if (handOff) note.appendChild(handOffLink());
    thread.appendChild(note);
    scrollToEnd();
  }

  /* ---- drawing ---- */

  function draw() {
    thread.replaceChildren();
    let lastQuestion = null;
    log.forEach((m, i) => {
      if (m.role === 'user') {
        lastQuestion = m;
        const turn = el('article', 'gg-shop__turn');
        if (m.pin) turn.appendChild(pinChip(m.pin));
        const q = el('div', 'gg-shop__q');
        q.appendChild(el('p', 'gg-shop__question', '“' + m.text + '”'));
        const isLatest = !log.slice(i + 1).some((x) => x.role === 'user');
        if (isLatest) {
          const edit = el('button', 'gg-shop__edit', 'Edit');
          edit.type = 'button';
          edit.dataset.shopperEdit = m.text;
          q.appendChild(edit);
        }
        turn.appendChild(q);
        thread.appendChild(turn);
      } else {
        const turn = thread.lastElementChild && thread.lastElementChild.classList.contains('gg-shop__turn') ? thread.lastElementChild : thread.appendChild(el('article', 'gg-shop__turn'));
        turn.appendChild(el('p', 'gg-shop__answer', m.text));
        if (m.lookedFor && m.lookedFor.length) turn.appendChild(looked(m.lookedFor));
        if (m.pieces && m.pieces.length) {
          const grid = el('ul', 'gg-shop__grid');
          m.pieces.forEach((p) => grid.appendChild(card(p)));
          turn.appendChild(grid);
        }
        if (m.handOff) turn.appendChild(handOffLink());
      }
    });
    // the newest answer's follow ups, one row that scrolls sideways and fades, never wraps
    chips.replaceChildren();
    const lastAnswer = [...log].reverse().find((m) => m.role === 'assistant');
    if (lastAnswer && lastQuestion && log[log.length - 1] === lastAnswer) {
      (lastAnswer.followUps || []).forEach((f) => {
        const chip = el('button', 'gg-chip', f);
        chip.type = 'button';
        chip.dataset.shopperChip = '';
        chips.appendChild(chip);
      });
    }
    chips.hidden = !chips.children.length;
    scrollToEnd();
  }

  function pinChip(p) {
    const chip = el('div', 'gg-shop__pin');
    if (p.image) {
      const img = el('img');
      img.src = p.image;
      img.alt = '';
      img.width = 32;
      img.height = 40;
      chip.appendChild(img);
    }
    const words = el('span', 'gg-shop__pin-words');
    words.appendChild(el('span', 'gg-shop__pin-kicker', 'About this piece'));
    words.appendChild(el('span', 'gg-shop__pin-title', p.title || p.handle));
    chip.appendChild(words);
    return chip;
  }

  function looked(pills) {
    const row = el('div', 'gg-shop__looked');
    row.appendChild(el('span', 'gg-shop__looked-label', 'What I looked for'));
    const rail = el('div', 'gg-shop__pills gg-fade-row');
    pills.forEach((p) => {
      const pill = el('button', 'gg-shop__pill');
      pill.type = 'button';
      pill.dataset.shopperDrop = withoutPill(p);
      pill.setAttribute('aria-label', 'Ask again without ' + p.label.toLowerCase() + ' ' + p.value);
      pill.appendChild(el('span', 'gg-shop__pill-key', p.label));
      pill.appendChild(document.createTextNode(' ' + p.value + ' '));
      pill.appendChild(el('span', 'gg-shop__pill-x', '×')).setAttribute('aria-hidden', 'true');
      rail.appendChild(pill);
    });
    const door = pills.map((p) => pillHref(p, searchUrl)).find(Boolean);
    if (door) {
      const a = el('a', 'gg-shop__advanced', 'Open in the advanced search →');
      a.href = door;
      rail.appendChild(a);
    }
    row.appendChild(rail);
    return row;
  }

  function handOffLink() {
    const a = el('a', 'gg-shop__handoff', 'Ask the house directly →');
    a.href = '#visit';
    return a;
  }

  function card(piece) {
    const item = el('li', 'gg-shop__card');
    const link = el('a', 'gg-shop__card-link');
    link.href = ROOT + 'products/' + encodeURIComponent(piece.handle);
    const frame = el('span', 'gg-shop__card-media');
    const img = el('img');
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    if (piece.image) img.src = sizedImage(piece.image, 480);
    frame.appendChild(img);
    link.appendChild(frame);
    const name = el('span', 'gg-shop__card-title', piece.title || piece.handle);
    link.appendChild(name);
    item.appendChild(link);
    const price = el('span', 'gg-shop__card-price');
    item.appendChild(price);
    if (piece.note) item.appendChild(el('p', 'gg-shop__card-note', piece.note));
    const actions = el('div', 'gg-shop__card-actions');
    item.appendChild(actions);
    live(piece.handle).then((o) => {
      const atEnd = shop.getBoundingClientRect && isNearEnd();
      if (!o) {
        actions.appendChild(viewKey(link.href, 'View the piece'));
      } else {
        link.href = o.url;
        name.textContent = o.title;
        if (o.image) img.src = sizedImage(o.image, 480);
        const amount = formatMoney(o.price, moneyFormat());
        price.textContent = o.available ? (o.variesInPrice ? 'From ' + amount : amount) : 'Sold out';
        if (!o.available) item.classList.add('is-sold');
        actions.appendChild(o.variantId ? addForm(o.variantId) : viewKey(o.url, o.choose ? 'Choose a size' : 'View the piece'));
      }
      if (atEnd) scrollToEnd();
    });
    return item;
  }

  function viewKey(href, label) {
    const a = el('a', 'gg-shop__key', label);
    a.href = href;
    return a;
  }

  function addForm(variantId) {
    const f = el('form');
    f.method = 'post';
    f.action = config.dataset.addUrl || ROOT + 'cart/add';
    f.setAttribute('data-gg-atb', '');
    const id = el('input');
    id.type = 'hidden';
    id.name = 'id';
    id.value = String(variantId);
    const qty = el('input');
    qty.type = 'hidden';
    qty.name = 'quantity';
    qty.value = '1';
    const key = el('button', 'gg-shop__key gg-shop__key--add');
    key.type = 'submit';
    key.appendChild(el('span', '', 'Add to bag')).setAttribute('data-atb-label', '');
    f.append(id, qty, key);
    f.addEventListener('submit', () => {
      cartStale = true;
      // the bag opens over the page: the room steps aside for it
      setTimeout(() => { const closer = search.querySelector('[data-search-close]'); if (closer) closer.click(); }, 0);
    });
    return f;
  }

  /** The store's own product JSON, once per piece per page. */
  function live(handle) {
    if (!products.has(handle)) {
      products.set(handle, fetch(ROOT + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .then(offerOf)
        .catch(() => null));
    }
    return products.get(handle);
  }

  function setStatus(text) {
    status.textContent = text || '';
    status.hidden = !text;
  }

  // the room scrolls (the search room is the page while it is open)
  const scroller = () => search.querySelector('.gg-search__room') || shop;
  function isNearEnd() {
    const s = scroller();
    return s.scrollHeight - s.scrollTop - s.clientHeight < 240;
  }
  function scrollToEnd() {
    requestAnimationFrame(() => {
      const s = scroller();
      s.scrollTop = s.scrollHeight;
    });
  }
})(typeof window !== 'undefined' ? window : globalThis);
